import ObjectComparer from 'common/services/util/-RFC.ObjectComparer';
import AppNotifications from 'common/notifications/-RFC.appNotifications';

let _appInstances = [];
const observableAppInstances = new Rx.BehaviorSubject(_appInstances);
const observableSingleInstances = [];

/**
     * @ngdoc service
     * @name repositories.AppInstancesRepository
     *
     * @description
     * This class is used to save/add app instances objects.
     */
const AppInstancesRepository = {

    replace(data) {

        _appInstances = data;
        observableAppInstances.onNext(_appInstances);
        observableSingleInstances.forEach((observableInstance) => {

            observableInstance.observable.onNext(data
                .find(newInstance => newInstance.id === observableInstance.id));

        });

    },

    /**
         * @ngdoc function
         * @name repositories.AppInstancesRepository#update
         * @methodOf repositories.AppInstancesRepository
         * @function
         *
         * @description
         * This function can be used to update an instance from repository.
         *
         * @param {object} instance an instance object with new values.
         */
    update(instance) {

        const indexOfInstance =
                _appInstances.findIndex(_instance => String(_instance.id) === String(instance.id));
        if (indexOfInstance >= 0) {

            const flattenDiff = ObjectComparer
                .getFlattenDiff(_appInstances[indexOfInstance], instance);
            AppNotifications.processUpdate(flattenDiff, this.getById(instance.id));

            _appInstances[indexOfInstance] = instance;

            observableAppInstances.onNext(_appInstances);
            const singleInstanceObservable = this.searchObservableFor(instance);
            if (singleInstanceObservable) {

                singleInstanceObservable.observable.onNext(instance);

            }

        }

    },

    /**
         * @ngdoc function
         * @name repositories.AppInstancesRepository#add
         * @methodOf repositories.AppInstancesRepository
         * @function
         *
         * @description
         * This function can be used to add a new instance to repository.
         *
         * @param {object} instance An instance object.
         */
    add(instance) {

        const indexOfInstance =
                _appInstances.findIndex(_instance => String(_instance.id) === String(instance.id));
        if (indexOfInstance === -1) {

            _appInstances.push(instance);
            observableAppInstances.onNext(_appInstances);

        }

    },

    /**
         * @ngdoc function
         * @name repositories.AppInstancesRepository#remove
         * @methodOf repositories.AppInstancesRepository
         * @function
         *
         * @description
         * This function can be used to remove an instance from repository.
         *
         * @param {integer} instanceId Id of the instance to be removed.
         */
    remove(instanceId) {

        const indexOfInstance = _appInstances
            .findIndex(_instance => String(_instance.id) === String(instanceId));

        if (indexOfInstance >= 0) {

            AppNotifications.processDelete(this.getById(instanceId));

            _appInstances.splice(indexOfInstance, 1);
            observableAppInstances.onNext(_appInstances);

            const singleInstanceObservableIndex = observableSingleInstances
                .findIndex((observableInstance) => {

                    if (observableInstance.id === instanceId) {

                        observableInstance.observable.onNext(undefined);
                        return true;

                    }

                    return false;

                });
            observableSingleInstances.splice(singleInstanceObservableIndex, 1);

        }

    },

    getAll() {

        return _appInstances;

    },

    /**
         * Returns the instance object after its id.
         * @param id
         */
    getById(id) {

        return _appInstances.find(appInstance => (String(appInstance.id) === String(id)));

    },

    /**
     * Returns the instance for a specific app and a specific device.
     * @param appId
     * @param deviceId
     */
    getByDeviceIdAndAppId(deviceId, appId) {

        return _appInstances.find((appInstance) => {

            if (appInstance.relationships.device) {

                return (String(appInstance.relationships.device.data.id) === String(deviceId) &&
                             String(appInstance.relationships.app.data.id) === String(appId));

            }

            return false;

        });

    },

    /**
         * Returns the instances of an user for a specific integration.
         * @param userId
         * @param integrationId
         */
    getByAppIntegration(userId, integrationId) {

        const instances = [];

        _appInstances.forEach((instance) => {

            if (String(instance.relationships.user.data.id) === String(userId) &&
                    String(instance.relationships.app.data.id) === String(integrationId)) {

                instances.push(instance);

            }

        });

        return instances;

    },

    getObservable() {

        return observableAppInstances;

    },

    /**
         * @ngdoc function
         * @name repositories.AppInstancesRepository#searchObservableFor
         * @methodOf repositories.AppInstancesRepository
         * @function
         *
         * @description
         * Searches for the observable object of an instance.
         *
         * @param {object} instance The instance object.
         *
         * @return {object} The observable object of the instance; undefined if it doesn't exist.
         */
    searchObservableFor(instance) {

        return observableSingleInstances
            .find(observableInstance => (String(observableInstance.id) === String(instance.id)));

    },

    /**
         * @ngdoc function
         * @name repositories.AppInstancesRepository#getObservableFor
         * @methodOf repositories.AppInstancesRepository
         * @function
         *
         * @description
         * Returns an observable object for an instance.
         *
         * @param {object} instance The instance object.
         *
         * @return {object} The observable object of the instance.
         */
    getObservableFor(instance) {

        const existingObservableInstance = this.searchObservableFor(instance);
        if (existingObservableInstance) {

            return existingObservableInstance.observable;

        }

        // create one if it doesn't exist
        const newObservableInstance = new Rx.Subject(instance);
        observableSingleInstances.push({ id: instance.id, observable: newObservableInstance });
        return newObservableInstance;

    },

};

export default AppInstancesRepository;



// WEBPACK FOOTER //
// ./scripts/common/repositories/-RFC.appInstancesRepository.js